import React, {useState} from "react";
import {
	TextField,
	Container,
	Box,
	Typography,
	MenuItem,
	Select,
	FormControl,
	InputLabel, Button,
} from "@mui/material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {ModifyReadySelect} from "../../../../components/common/modify-ready-select/ModifyReadySelect";
import {JobSettingsForm} from "../job-settings/JobSettingsForm";
import {GET_JOB_SETTINGS} from "../graphql/queries/gqlGetJobSettings";
import {IJobSettings} from "../../models/JobSettings";
import {IJobDefinitionStep, Processor, StartMode} from "../../models/JobDefinitionStep";
import {JobSettingsEditor} from "../job-settings/JobSettingsEditor";
import {GenericModal} from "../generic-modal/GenericModal";

interface IJobDefinitionStepProps {
	step: IJobDefinitionStep;
	setStep: (step: IJobDefinitionStep) => void;
	steps: IJobDefinitionStep[];
	jobDefinitionId: number;
}

export const JobDefinitionStepForm: React.FC<IJobDefinitionStepProps> = (props) => {
	const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		props.setStep({
			...props.step,
			[event.target.name]: event.target.type === "number" ? +event.target.value : event.target.value,
		});
	};
	
	const handleSettingsChange = (value: number) => {
		props.setStep({...props.step, customSettings: undefined, jobSettings: {id: value, settings: "", description: ""}});
	};
	
	const handleCustomEdit = () => {
		setIsCustomModalOpen(true);
	};
	const handleCustomClose = () => {
		setIsCustomModalOpen(false);
	};
	
	const handleSetSettings = (settings: string) => {
		props.setStep({...props.step, customSettings: settings});
	};
	
	const handleInputStepChange = (value: number) => {
		if (value !== 0) {
			props.setStep({...props.step, inputStep: props.steps.filter(it => it.id === value)[0]});
		} else {
			props.setStep({...props.step, inputStep: undefined});
		}
	};
	
	const fetchJobSettings = async (): Promise<IJobSettings[]> => {
		return (await graphQLServices.querytoolClient
			.query({
				query: GET_JOB_SETTINGS,
			})).data.jobSettings.nodes;
	};
	
	return (
		<Container maxWidth="sm">
			<Box sx={{ padding: 3 }}>
				<Typography variant="h4" component="h1" align="center" gutterBottom>
					Post a Job Step
				</Typography>
				<form>
					<TextField
						fullWidth
						label="ID"
						name="id"
						value={props.step.id}
						onChange={handleChange}
						margin="normal"
						type="number"
						required
						disabled
					/>
					<TextField
						fullWidth
						label="Job Definition"
						name="jobDefinition"
						value={props.jobDefinitionId}
						onChange={handleChange}
						margin="normal"
						type="number"
						required
						disabled
					/>
					<TextField
						fullWidth
						label="Concurrency"
						name="concurrency"
						value={props.step.concurrency}
						onChange={handleChange}
						margin="normal"
						type="number"
						required
					/>
					<TextField
						fullWidth
						label="Order"
						name="order"
						value={props.step.order}
						onChange={handleChange}
						margin="normal"
						type="number"
						required
					/>
					<TextField
						fullWidth
						label="Description"
						name="description"
						value={props.step.description}
						onChange={handleChange}
						margin="normal"
						required
					/>
					<FormControl fullWidth margin="normal" required>
						<InputLabel htmlFor="inputStep">Input Step</InputLabel>
						<Select
							label="Input Step"
							name="inputStep"
							value={props.step.inputStep?.id ?? 0}
							onChange={(event) => handleInputStepChange(+event.target.value)}
						>
							<MenuItem key={"undefined-value-1dfsdafdsf"} value={0}>
								None
							</MenuItem>
							{props.steps.map((jobDefinitionStep) => (
								<MenuItem key={jobDefinitionStep.id} value={jobDefinitionStep.id}>
									{jobDefinitionStep.description}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl fullWidth margin="normal" required>
						<InputLabel htmlFor="processor">Processor</InputLabel>
						<Select
							label="Processor"
							name="processor"
							value={props.step.processor}
							onChange={handleChange as any}
						>
							{Object.values(Processor).map((processor) => (
								<MenuItem key={processor} value={processor}>
									{processor}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl fullWidth margin="normal" required>
						<ModifyReadySelect
							fetch={fetchJobSettings}
							keySelector={"id"}
							onChange={handleSettingsChange}
							labelSelector={"description"}
							modifyContent={(key) => <JobSettingsForm id={key as number} processor={props.step.processor} />}
							createContent={<JobSettingsForm processor={props.step.processor} />}
							label={"Select a predefiend setting"}
							defaultValue={props.step.jobSettings?.id}
						/>
						{props.step.jobSettings?.id === undefined &&
							<>
								<label>or</label>
								<Button variant="contained" color="secondary" onClick={handleCustomEdit}>
							Custom setting
								</Button>
							</>
						}
					</FormControl>
					<FormControl fullWidth margin="normal" required>
						<InputLabel htmlFor="startMode">Start Mode</InputLabel>
						<Select
							label="Start Mode"
							name="startMode"
							value={props.step.startMode}
							onChange={handleChange as any}
						>
							{Object.values(StartMode).map((startMode) => (
								<MenuItem key={startMode} value={startMode}>
									{startMode}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</form>
			</Box>
			<GenericModal
				open={isCustomModalOpen}
				onClose={handleCustomClose}
				title={"Settings"}
			>
				<JobSettingsEditor processor={props.step.processor} setSettings={handleSetSettings} settings={props.step.customSettings ?? ""}/>
			</GenericModal>
		</Container>
		
	);
};
